import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import profile from "../assets/profile/user.png";
import { ReactComponent as DashboardIcon } from "../assets/dashboard-icon.svg";
import { ReactComponent as InventoryIcon } from "../assets/inventory-icon.svg";
import { ReactComponent as OrderIcon } from "../assets/order-icon.svg";
import { ReactComponent as PurchaseIcon } from "../assets/purchase-icon.svg";
import { ReactComponent as WalletIcon } from "../assets/supplier-icon.svg";

const TrackingIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
  </svg>
);

const BarcodeMenuIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zm0 9.75c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zm9.75-9.75c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 6.75h.75v.75h-.75v-.75zm0 9.75h.75v.75h-.75v-.75zm9.75-9.75h.75v.75h-.75v-.75z" />
  </svg>
);

const ReturnsIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
  </svg>
);

const NAV_ITEMS = [
  { to: "/", label: "Dashboard", Icon: DashboardIcon },
  { to: "/supplyer", label: "Supplier", Icon: OrderIcon },
  { to: "/products", label: "Products", Icon: InventoryIcon },
  { to: "/purchase", label: "Purchase", Icon: PurchaseIcon },
  { to: "/expense", label: "Expense", Icon: WalletIcon },
  { to: "/returns", label: "Returns", Icon: ReturnsIcon },
  { to: "/barcode", label: "Barcode", Icon: BarcodeMenuIcon },
  { to: "/tracking", label: "Tracking", Icon: TrackingIcon },
];

const REPORT_ITEMS = [
  { to: "/reports/product", label: "Products Report" },
  { to: "/reports/purchase", label: "Purchase Report" },
  { to: "/reports/expense", label: "Expense Report" },
];

const ReportsIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const UsersIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" stroke="currentColor" strokeWidth="2" fill="none" />
  </svg>
);

const SettingsIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

function NavLink({ to, label, Icon, active, onClick }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
        active
          ? "bg-blue-600 text-white shadow-md shadow-blue-600/30"
          : "text-slate-400 hover:bg-slate-700/60 hover:text-white"
      }`}
    >
      {Icon && (
        <Icon
          className={`w-5 h-5 shrink-0 transition-colors duration-200 ${
            active ? "text-white" : "text-slate-400 group-hover:text-white"
          }`}
        />
      )}
      <span>{label}</span>
      {active && (
        <span className="ml-auto h-1.5 w-1.5 rounded-full bg-white/70" />
      )}
    </Link>
  );
}

function SideMenu({ onNavigate }) {
  const localStorageData = JSON.parse(localStorage.getItem("user"));
  const location = useLocation();
  const [reportsOpen, setReportsOpen] = useState(
    location.pathname.startsWith("/reports")
  );

  const isActive = (path) => location.pathname === path;
  const reportsActive = location.pathname.startsWith("/reports");

  return (
    <div className="h-full flex flex-col bg-slate-900 w-full border-r border-slate-800">
      {/* Logo area */}
      <div className="px-5 py-5 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 shadow-lg shadow-blue-600/40 shrink-0">
            <img
              src={require("../assets/janani_logo.jpg")}
              alt="logo"
              className="h-6 w-6 rounded object-cover"
            />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-white leading-tight truncate">Janani Group</p>
            <p className="text-xs text-slate-400 truncate">Motor Parts Tracker</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
          Main
        </p>

        {NAV_ITEMS.map(({ to, label, Icon }) => (
          <NavLink key={to} to={to} label={label} Icon={Icon} active={isActive(to)} onClick={onNavigate} />
        ))}

        <div className="pt-3">
          <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
            Analytics
          </p>

          {/* Reports accordion */}
          <button
            onClick={() => setReportsOpen((v) => !v)}
            className={`group w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
              reportsActive
                ? "bg-blue-600 text-white shadow-md shadow-blue-600/30"
                : "text-slate-400 hover:bg-slate-700/60 hover:text-white"
            }`}
          >
            <ReportsIcon
              className={`w-5 h-5 shrink-0 ${
                reportsActive ? "text-white" : "text-slate-400 group-hover:text-white"
              }`}
            />
            <span>Reports</span>
            <svg
              className={`ml-auto h-4 w-4 transition-transform duration-200 ${
                reportsOpen ? "rotate-180" : ""
              } ${reportsActive ? "text-white" : "text-slate-500"}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {reportsOpen && (
            <div className="mt-1 ml-4 pl-4 border-l border-slate-700 space-y-0.5">
              {REPORT_ITEMS.map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={onNavigate}
                  className={`block rounded-lg px-3 py-2 text-sm transition-colors duration-200 ${
                    isActive(to)
                      ? "text-blue-400 font-semibold"
                      : "text-slate-400 hover:text-white hover:bg-slate-700/50"
                  }`}
                >
                  {label}
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="pt-3">
          <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
            System
          </p>
          <NavLink to="/users" label="Users" Icon={UsersIcon} active={isActive("/users")} onClick={onNavigate} />
          <NavLink to="/setting" label="Settings" Icon={SettingsIcon} active={isActive("/setting")} onClick={onNavigate} />
        </div>
      </nav>

      {/* User profile footer */}
      <div className="px-3 py-4 border-t border-slate-800">
        <div className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-slate-800 transition-colors duration-200 cursor-pointer">
          <img
            alt="Profile"
            src={profile}
            className="h-8 w-8 rounded-full object-cover ring-2 ring-slate-700 shrink-0"
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-white truncate">
              {localStorageData?.name || "User"}
            </p>
            <p className="text-xs text-slate-400 truncate">
              {localStorageData?.email || ""}
            </p>
          </div>
          <div className="h-2 w-2 rounded-full bg-emerald-400 shrink-0" title="Online" />
        </div>
      </div>
    </div>
  );
}

export default SideMenu;
